import { apiRequest } from "./baseApi";
import { parseJsonOrThrow } from "./responseUtils";

/**
 * GMA-1571 — Apple Watch integration: per-client admin toggle + the health
 * metrics that feed the real-time vitals pane on the client profile.
 */

/** Admin on/off toggle. PATCH /api/v3/clients/:clientId/watch-integration */
export const setWatchIntegrationEnabled = async (clientId, enabled) => {
  const res = await apiRequest(`clients/${clientId}/watch-integration`, {
    version: "v3",
    method: "PATCH",
    body: { enabled },
  });
  return parseJsonOrThrow(res, "Watch integration toggle");
};

/**
 * GMA-1865 — authoritative read of the per-client Watch toggle.
 * GET /api/v3/clients/:clientId/watch-integration
 * Prefer this over the client roster field so admin on/off cannot snap back
 * when `appleWatchEnabled` is missing/stale on GET /clients.
 */
export const fetchWatchIntegrationEnabled = async (clientId) => {
  const res = await apiRequest(`clients/${clientId}/watch-integration`, {
    version: "v3",
  });
  const data = await parseJsonOrThrow(res, "Watch integration status");
  return data?.appleWatchEnabled === true;
};

/**
 * Recent health metrics for the live vitals pane. The caller reduces the
 * stream to the latest sample per type. GET /api/v1/users/:id/health/metrics
 */
export const fetchClientHealthMetrics = async (clientId, { limit = 100 } = {}) => {
  const res = await apiRequest(
    `users/${clientId}/health/metrics?limit=${encodeURIComponent(limit)}`,
    { version: "v1" },
  );
  return parseJsonOrThrow(res, "Watch vitals");
};

/**
 * #437 / cortex-be#639 — latest reading per metric type, burst-proof: the
 * backend reduces with DISTINCT ON so a sync burst of one type can never
 * crowd other types out of a row window.
 * GET /api/v1/users/:id/health/metrics/latest?days=7
 */
export const fetchClientLatestHealthMetrics = async (clientId, { days = 7 } = {}) => {
  const res = await apiRequest(
    `users/${clientId}/health/metrics/latest?days=${encodeURIComponent(days)}`,
    { version: "v1" },
  );
  return parseJsonOrThrow(res, "Watch vitals (latest per type)");
};

/**
 * GMA-1579 — recent abnormal-vitals caregiver alerts (the Watch pane's alert
 * history). These deliver via email + push but not the web bell, so the pane is
 * the web caregiver's view of them. GET /api/v3/clients/:clientId/vitals-alerts
 */
export const fetchClientVitalsAlerts = async (clientId, { limit = 20 } = {}) => {
  const res = await apiRequest(
    `clients/${clientId}/vitals-alerts?limit=${encodeURIComponent(limit)}`,
    { version: "v3" },
  );
  return parseJsonOrThrow(res, "Watch vitals alerts");
};

/**
 * Issue #633 (cortex-be) — caregiver clears a vitals alert. The backend stamps
 * acknowledged_at/acknowledged_by (audit trail — the row is never deleted).
 * POST /api/v3/clients/:clientId/vitals-alerts/:alertId/acknowledge
 */
export const acknowledgeVitalsAlert = async (clientId, alertId) => {
  const res = await apiRequest(
    `clients/${clientId}/vitals-alerts/${encodeURIComponent(alertId)}/acknowledge`,
    { version: "v3", method: "POST" },
  );
  return parseJsonOrThrow(res, "Clear vitals alert");
};

/**
 * GMA-1581 — categorical HealthKit signals (ECG result, AFib episode, irregular
 * rhythm, sleep-apnea / hypertension notifications, sleep stage). Numeric vitals
 * come from /health/metrics; the categorical signals are surfaced by the
 * GMA-1572 health-vitals lookup. GET /api/v3/ai/health-vitals/:clientId
 * Returns the `signals` array (newest per signal_type), or [] when none.
 */
export const fetchClientHealthSignals = async (clientId) => {
  const res = await apiRequest(`ai/health-vitals/${clientId}`, { version: "v3" });
  const data = await parseJsonOrThrow(res, "Watch health signals");
  return Array.isArray(data?.signals) ? data.signals : [];
};

/**
 * GMA-1588 — per-client vitals alert thresholds. Admin only. The backend stores
 * overrides in ClientHealthConfig.customThresholds keyed `<metricType>_<bound>`
 * (bound = warn_below | warn_above | crit_below | crit_above); any key left
 * unset falls back to the medically-conservative baseline in
 * vitalsThresholdEngine. GET /api/v1/users/:id/health/config
 */
export const fetchClientHealthConfig = async (clientId) => {
  const res = await apiRequest(`users/${clientId}/health/config`, {
    version: "v1",
  });
  const data = await parseJsonOrThrow(res, "Watch health config");
  return data?.config || {};
};

/**
 * GMA-1588 — save per-client threshold overrides (and other config fields).
 * PUT /api/v1/users/:id/health/config. `customThresholds` is replaced wholesale
 * by the backend, so callers must send the merged object (preserving keys they
 * do not manage, e.g. signalSeverity).
 */
export const updateClientHealthConfig = async (clientId, updates) => {
  const res = await apiRequest(`users/${clientId}/health/config`, {
    version: "v1",
    method: "PUT",
    body: updates,
  });
  const data = await parseJsonOrThrow(res, "Update watch health config");
  return data?.config || {};
};

/** Reduce a metrics list (recorded_at DESC) to the newest sample per type. */
export const latestByMetricType = (metrics = []) => {
  const latest = new Map();
  for (const m of metrics) {
    const prev = latest.get(m.metricType);
    if (!prev || new Date(m.recordedAt) > new Date(prev.recordedAt)) {
      latest.set(m.metricType, m);
    }
  }
  return latest;
};
